import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import axios from "axios";
import { CartProvider, useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { AUTH_CLEARED_EVENT, authRevision } from "@/lib/authSession";
import CartDrawer from "@/components/CartDrawer";
import Pagination from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, FileText, DollarSign, LogOut, Lock, ShieldCheck, Package, Search, Menu, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useResponsivePageSize } from "../hooks/useResponsivePageSize";
import { fetchAvailability } from "@/lib/reservation";
import { canPlaceWholesaleOrder, isReadOnlyGuest } from "@/lib/roles";

const API = process.env.REACT_APP_BACKEND_URL;

const stockBadge = (n) => {
  if (n <= 0) return { label: "Out of stock", cls: "bg-red-500/15 text-red-300 border-red-500/30" };
  if (n < 50) return { label: `Low stock · ${n}`, cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" };
  return { label: `In stock · ${n}`, cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" };
};

const ProductCard = React.memo(function ProductCard({
  p,
  available,
  isDP,
  isRegisteredClient,
  canPlaceOrders,
  invoiceQty,
  onAddToCart,
  onInvoiceQtyChange,
}) {
  const reserved = available <= 0;
  const b = reserved ? { label: "Reserved", cls: "bg-gray-500/20 text-gray-300 border-gray-500/40" } : stockBadge(p.warehouse_stock);
  return (
    <div
      className="group rounded-xl overflow-hidden bg-[#171B26] border border-[#2A2F3C] hover:border-[#D4AF37]/50 hover:shadow-[0_8px_30px_rgba(0,0,0,0.5)] transition-all duration-300 flex flex-col"
      data-testid={`product-${p.id}`}
    >
      <div className="relative aspect-square bg-[#0E1119] overflow-hidden">
        {p.image_url
          ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          : <div className="w-full h-full flex items-center justify-center text-[#2A2F3C]"><Package size={40} /></div>}
        <span className={`absolute top-2 left-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border backdrop-blur-sm ${b.cls}`} data-testid={`stock-badge-${p.id}`}>
          {b.label}
        </span>
      </div>
      <div className="p-2.5 sm:p-3 flex flex-col flex-1">
        <h3 className="font-semibold capitalize text-white text-xs sm:text-sm leading-tight line-clamp-2" data-testid={`product-name-${p.id}`}>{p.name}</h3>
        <div className="mt-1.5 flex items-baseline gap-1">
          <span className="text-lg sm:text-xl font-extrabold tabular-nums text-[#F5D06B]" data-testid={`wholesale-price-${p.id}`}>${Number(p.wholesale_price).toFixed(2)}</span>
          <span className="text-[10px] text-gray-500 font-medium">/ unit</span>
        </div>
        <div className="mt-auto pt-3 flex items-center gap-1.5">
          {!isDP && <Button
            size="sm"
            onClick={() => onAddToCart(p)}
            disabled={p.warehouse_stock <= 0 || reserved || !canPlaceOrders}
            className="flex-1 h-8 px-2 bg-[#D4AF37] hover:bg-[#c39f2c] text-[#0B0E17] font-bold text-xs disabled:opacity-40"
            data-testid={`ws-cart-add-${p.id}`}
          >
            <Plus size={12} className="mr-0.5" /> {reserved ? "Reserved" : !canPlaceOrders ? "View only" : "Cart"}
          </Button>}
          {!isRegisteredClient && <div className="flex items-center rounded-md border border-[#2A2F3C] bg-[#0E1119] overflow-hidden shrink-0">
            <span className="px-1.5 text-[9px] font-bold uppercase tracking-wider text-gray-500">Inv</span>
            <button type="button" className="h-8 w-7 text-[#D4AF37]" onClick={() => onInvoiceQtyChange(p.id, p.warehouse_stock, invoiceQty - 1)} aria-label="Decrease invoice quantity" data-testid={`ws-invoice-down-${p.id}`}>⌄</button>
            <Input
              type="text" inputMode="numeric" pattern="[0-9]*"
              className="w-11 h-8 border-0 bg-transparent text-gray-100 text-center px-0 text-sm focus-visible:ring-1 focus-visible:ring-[#D4AF37]"
              value={invoiceQty || ""}
              placeholder="0"
              disabled={!canPlaceOrders}
              onChange={(e) => onInvoiceQtyChange(p.id, p.warehouse_stock, e.target.value.replace(/\D/g, ""))}
              title="Qty for Due Upon Receipt invoice order"
              data-testid={`ws-invoice-qty-${p.id}`}
            />
            <button type="button" className="h-8 w-7 text-[#D4AF37]" onClick={() => onInvoiceQtyChange(p.id, p.warehouse_stock, invoiceQty + 1)} aria-label="Increase invoice quantity" data-testid={`ws-invoice-up-${p.id}`}>⌃</button>
          </div>}
        </div>
      </div>
    </div>
  );
});

function WholesaleBody() {
  const { add, setContext, count } = useCart();
  const { user, logout } = useAuth();
  const readOnlyGuest = isReadOnlyGuest(user);
  const isDP = user?.role === "distributing_partner";
  const isRegisteredClient = user?.role === "client";
  const canPlaceOrders = canPlaceWholesaleOrder(user);
  const orderingLabel = readOnlyGuest ? "Read-only" : user ? "Order access required" : "Sign in to order";
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [invoiceQty, setInvoiceQty] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [clientSession, setClientSession] = useState(null);
  const [mobileInvoiceOpen, setMobileInvoiceOpen] = useState(false);
  const [dpHold, setDpHold] = useState(null);
  const [dpSeconds, setDpSeconds] = useState(0);
  const [dpInvoiceError, setDpInvoiceError] = useState("");
  const [dpProfileDiscount, setDpProfileDiscount] = useState(null);

  // Public-store-style browsing state
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [page, setPage] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const PAGE_SIZE = useResponsivePageSize(6, 24);

  const updateInvoiceQty = useCallback((productId, maxStock, nextValue) => {
    const requested = Number(nextValue) || 0;
    const quantity = Math.max(0, Math.min(maxStock, requested));
    setInvoiceQty((current) => {
      if (Number(current[productId] || 0) === quantity) return current;
      return { ...current, [productId]: quantity };
    });
  }, []);

  const addToCart = useCallback((product) => add(product, 1), [add]);

  useEffect(() => {
    setContext("wholesale");
    api.get("/products/public").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/clients/public").then((r) => setClients(r.data)).catch(() => setClients([]));
    const tok = localStorage.getItem("client_token");
    const version = authRevision();
    if (tok) {
      axios.get(`${API}/api/client/me`, { headers: { Authorization: `Bearer ${tok}` } })
        .then((r) => { if (version === authRevision()) setClientSession(r.data); })
        .catch(() => { localStorage.removeItem("client_token"); localStorage.removeItem("client_id"); });
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    const cleared = () => setClientSession(null);
    window.addEventListener(AUTH_CLEARED_EVENT, cleared);
    return () => window.removeEventListener(AUTH_CLEARED_EVENT, cleared);
  }, []);

  const clientLogout = async () => {
    setClientSession(null);
    const revoked = await logout();
    if (revoked) toast.success("Signed out");
    else toast.error("Signed out locally. Session cleanup will retry when you sign in.");
  };

  // ---- Brand/category filter + pagination (mirrors the Public Store) ----
  const cats = useMemo(() => {
    const s = new Set();
    products.forEach((p) => (p.categories || "").split(",").map((c) => c.trim()).filter(Boolean).forEach((c) => s.add(c)));
    return Array.from(s).sort();
  }, [products]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return products.filter((p) => {
      if (needle && !`${p.name} ${p.sku || ""}`.toLowerCase().includes(needle)) return false;
      if (cat && !(p.categories || "").toLowerCase().includes(cat.toLowerCase())) return false;
      return true;
    });
  }, [products, q, cat]);

  useEffect(() => { setPage(0); }, [q, cat]);

  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const countFor = (c) => products.filter((p) => (p.categories || "").toLowerCase().includes(c.toLowerCase())).length;

  // Live reservation availability for the visible page.
  const [avail, setAvail] = useState({});
  const pagedIds = paged.map((p) => p.id).join(",");
  useEffect(() => {
    const ids = pagedIds ? pagedIds.split(",") : [];
    if (!ids.length) return;
    let active = true;
    const load = () => fetchAvailability(ids).then((d) => { if (active) setAvail(d); });
    load();
    window.addEventListener("cart-updated", load);
    const t = setInterval(load, 20000);
    return () => { active = false; window.removeEventListener("cart-updated", load); clearInterval(t); };
  }, [pagedIds]);

  const invoiceTotal = useMemo(() => {
    return Object.entries(invoiceQty).reduce((s, [pid, qty]) => {
      const p = products.find((x) => x.id === pid);
      return s + (p ? Number(p.wholesale_price) * Number(qty) : 0);
    }, 0);
  }, [invoiceQty, products]);

  const invoiceLineCount = Object.values(invoiceQty).filter((q) => q > 0).length;
  const dpDiscount = Number(dpProfileDiscount || 0);
  const displayedInvoiceTotal = isDP ? invoiceTotal * (1 - dpDiscount / 100) : invoiceTotal;

  useEffect(() => {
    if (!isDP) return undefined;
    api.get("/dp/wholesale-invoice/context").then(({ data }) => { setDpProfileDiscount(Number(data.discount_percent || 0)); setDpInvoiceError(""); }).catch((err) => setDpInvoiceError(formatApiErrorDetail(err.response?.data?.detail) || "Wholesale Discount must be set in your DP profile before you can place an order"));
    return undefined;
  }, [isDP]);

  useEffect(() => {
    if (!isDP) return undefined;
    const items = Object.entries(invoiceQty).filter(([, qty]) => qty > 0).map(([product_id, qty]) => ({ product_id, qty: Number(qty) }));
    const timer = setTimeout(async () => {
      try {
        if (!items.length) { await api.post("/dp/wholesale-invoice/release"); setDpHold(null); return; }
        const { data } = await api.post("/dp/wholesale-invoice/reserve", { items });
        setDpSeconds(data?.expires_at ? Math.max(0, Math.ceil((new Date(data.expires_at).getTime() - Date.now()) / 1000)) : 0);
        setDpHold(data); setDpInvoiceError("");
      } catch (err) { const message = formatApiErrorDetail(err.response?.data?.detail) || "Unable to hold invoice inventory"; setDpInvoiceError(message); toast.error(message); }
    }, 300);
    return () => clearTimeout(timer);
  }, [isDP, invoiceQty]);

  useEffect(() => {
    if (!dpHold?.expires_at) { setDpSeconds(0); return undefined; }
    const tick = () => setDpSeconds(Math.max(0, Math.ceil((new Date(dpHold.expires_at).getTime() - Date.now()) / 1000)));
    tick(); const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [dpHold?.expires_at]);

  useEffect(() => {
    if (!isDP || !dpHold?.expires_at || new Date(dpHold.expires_at).getTime() > Date.now()) return;
    api.post("/dp/wholesale-invoice/release").finally(() => { setDpHold(null); setInvoiceQty({}); });
  }, [isDP, dpHold, dpSeconds]);

  const placeInvoiceOrder = async () => {
    if (!canPlaceOrders) { toast.error("Wholesale ordering requires an Admin, Distributing Partner, or registered client account"); return; }
    const lines = Object.entries(invoiceQty).filter(([, q]) => q > 0).map(([pid, qty]) => {
      const p = products.find((x) => x.id === pid);
      return { product_id: pid, name: p.name, qty: Number(qty), unit_price: p.wholesale_price };
    });
    if ((!isDP && !clientId) || lines.length === 0) { toast.error(isDP ? "Add at least one item" : "Select a client and add at least one item"); return; }
    setSubmitting(true);
    try {
      const { data } = isDP
        ? await api.post("/dp/wholesale-invoice", { items: lines })
        : await api.post("/orders/public", { client_id: clientId, items: lines, notes: "" });
      setSuccess(data); setInvoiceQty({}); setMobileInvoiceOpen(false);
      setDpHold(null);
      toast.success("Invoice created · Due Upon Receipt");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const selectedClient = clients.find((c) => c.id === clientId);

  const Net30Panel = () => (
    <>
      <div className="flex items-center gap-2 mb-2">
        <FileText size={14} className="text-[#D4AF37]" />
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Wholesale Invoice · Due Upon Receipt</p>
      </div>
      <p className="text-[11px] text-gray-400 mb-4 leading-relaxed">
        {isDP ? "Your invoice is assigned to you and transfers held inventory into your DP stock." : <>Prefer to pay later? Select a client and place an invoice order — no card needed. Item quantities come from the <span className="text-gray-300 font-medium">"Inv"</span> boxes on each product.</>}
      </p>
      {!isDP && !isRegisteredClient && <><Label className="text-gray-300 text-xs">Client</Label>
      <Select value={clientId || undefined} onValueChange={setClientId} disabled={!canPlaceOrders}>
        <SelectTrigger data-testid="client-select" className="bg-[#0E1119] border-[#2A2F3C] text-gray-100 mt-1"><SelectValue placeholder="Select client" /></SelectTrigger>
        <SelectContent>
          {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` (${c.company})` : ""}</SelectItem>)}
        </SelectContent>
      </Select></>}
      {isDP && <div className="mb-3 rounded-md border border-[#D4AF37]/35 bg-[#D4AF37]/10 px-3 py-2 text-xs" data-testid="dp-invoice-discount">Wholesale Discount <span className="float-right font-bold text-[#F5D06B]">{dpDiscount.toFixed(1)}%</span></div>}
      {isDP && dpInvoiceError && <p className="mb-3 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200" data-testid="dp-invoice-error">{dpInvoiceError}</p>}
      <div className="mt-4 space-y-1 text-sm max-h-40 overflow-y-auto">
        {Object.entries(invoiceQty).filter(([, q]) => q > 0).map(([pid, q]) => {
          const p = products.find((x) => x.id === pid);
          return p ? (
            <div key={pid} className="flex justify-between text-xs text-gray-300">
              <span className="truncate capitalize">{p.name} × {q}</span>
              <span className="tabular-nums text-[#F5D06B]">${(p.wholesale_price * q).toFixed(2)}</span>
            </div>
          ) : null;
        })}
      </div>
      <div className="border-t border-[#2A2F3C] mt-4 pt-3 flex justify-between font-semibold text-gray-100">
        <span>Total</span>
        <span className="tabular-nums text-[#F5D06B] text-lg" data-testid="invoice-total">${displayedInvoiceTotal.toFixed(2)}</span>
      </div>
      {isDP && <div className="mt-1 flex justify-between text-xs text-emerald-300" data-testid="dp-invoice-discount-amount"><span>Wholesale discount</span><span>−${(invoiceTotal - displayedInvoiceTotal).toFixed(2)}</span></div>}
      <Button
        className="w-full mt-4 bg-[#D4AF37] hover:bg-[#c39f2c] text-[#0B0E17] font-bold"
        disabled={submitting || !canPlaceOrders}
        onClick={placeInvoiceOrder}
        data-testid="place-invoice-order"
      >
        <FileText size={14} className="mr-1.5" /> {submitting ? "Placing…" : "Place invoice order"}
      </Button>
      {success && (
        <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded text-sm text-emerald-200">
          Order placed! Status: <strong>{success.status}</strong>
          {success.fulfilled_by_name && <> · Assigned to <strong>{success.fulfilled_by_name}</strong></>}
        </div>
      )}
      {count > 0 && (
        <p className="mt-4 text-[11px] text-center text-gray-400">
          {!isDP && <>Or <span className="text-[#D4AF37] font-semibold">{count}</span> item{count === 1 ? "" : "s"} in your Stripe cart above ↑</>}
        </p>
      )}
    </>
  );

  return (
    <div className="h-screen w-screen flex overflow-hidden bg-[#0B0E17] text-gray-100" data-testid="wholesale-portal">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setMobileOpen(false)} data-testid="wholesale-sidebar-backdrop" />
      )}

      {/* LEFT — brand/category filter sidebar (mirrors the Public Store) */}
      <aside
        className={`w-64 lg:w-60 flex flex-col h-full shrink-0 bg-[#0B0E17] border-r border-[#2A2F3C]
                    fixed inset-y-0 left-0 z-40 transform transition-transform duration-200
                    lg:static lg:translate-x-0
                    ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="wholesale-sidebar"
      >
        <Link to="/login" className="px-4 py-5 border-b border-[#2A2F3C] flex items-center gap-3 hover:bg-white/5 transition-colors" data-testid="wholesale-logo-link">
          <img src="/brand/mpire-logo.png" alt="MPIRE Distribution" className="w-12 h-12 object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]" />
          <div className="flex flex-col leading-tight">
            <span className="font-bold tracking-tight text-white text-[15px]">MPIRE Distribution</span>
            <span className="text-[9px] uppercase tracking-[0.22em] text-[#D4AF37]">Wholesale Portal</span>
          </div>
        </Link>

        <div className="px-4 pt-4 pb-2">
          <p className="text-[10px] uppercase tracking-[0.22em] text-gray-500 font-semibold">Brands</p>
        </div>

        <nav className="flex-1 p-3 pt-1 space-y-1 overflow-y-auto" data-testid="wholesale-brand-nav">
          <button
            onClick={() => { setCat(""); setMobileOpen(false); }}
            className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${!cat ? "bg-[#D4AF37]/15 text-[#D4AF37] font-semibold" : "text-gray-300 hover:bg-white/5"}`}
            data-testid="brand-all"
          >
            <Sparkles size={14} />
            <span className="flex-1">All brands</span>
            <span className="text-[10px] opacity-60 tabular-nums">{products.length}</span>
          </button>
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => { setCat(cat === c ? "" : c); setMobileOpen(false); }}
              className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm capitalize transition-colors ${cat === c ? "bg-[#D4AF37]/15 text-[#D4AF37] font-semibold" : "text-gray-300 hover:bg-white/5"}`}
              data-testid={`brand-${c.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <span className="flex-1 truncate">{c}</span>
              <span className="text-[10px] opacity-60 tabular-nums shrink-0">{countFor(c)}</span>
            </button>
          ))}
        </nav>

        <div className="border-t border-[#2A2F3C] p-4 text-[11px] text-gray-500">
          <span className="inline-flex items-center gap-1.5 text-emerald-300"><ShieldCheck size={12} /> Verified Client Access</span>
        </div>
      </aside>

      {/* MAIN */}
      <main
        className="flex-1 min-w-0 h-screen overflow-y-auto overflow-x-hidden flex flex-col"
        style={{ backgroundImage: "radial-gradient(1200px 500px at 80% -10%, rgba(212,175,55,0.10), transparent 60%)" }}
      >
        {/* Sticky top bar: search + cart */}
        <header className="sticky top-0 z-20 bg-[#0B0E17]/90 backdrop-blur border-b border-[#2A2F3C]">
          <div className="px-3 sm:px-6 py-3 flex items-center gap-3 sm:gap-4">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 -ml-2 rounded-md hover:bg-white/10"
              aria-label="Open menu"
              data-testid="wholesale-mobile-menu-button"
            >
              <Menu size={22} />
            </button>
            <div className="flex-1" />
            <div className="relative flex-shrink-0">
              <Search size={14} className="absolute left-3 top-3 text-gray-400" />
              <Input
                className="pl-8 w-40 sm:w-72 bg-[#0E1119] border-[#2A2F3C] text-gray-100 placeholder:text-gray-500"
                placeholder="Search products…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="wholesale-search"
              />
            </div>
            {clientSession && (
              <div className="hidden sm:flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full bg-[#171B26] border border-[#D4AF37]/40" data-testid="mpire-bucks-widget">
                <div className="w-6 h-6 rounded-full bg-[#D4AF37] text-[#0B0E17] flex items-center justify-center">
                  <DollarSign size={12} strokeWidth={3} />
                </div>
                <div className="leading-tight">
                  <div className="text-[9px] uppercase tracking-wider font-semibold text-[#D4AF37]">MPire Buck$</div>
                  <div className="text-sm font-bold tabular-nums text-white" data-testid="mpire-bucks-balance">
                    ${Number(clientSession.mpire_bucks_balance || 0).toFixed(2)}
                  </div>
                </div>
                <button onClick={clientLogout} className="ml-1 p-1 text-gray-400 hover:text-[#D4AF37]" title="Sign out" data-testid="client-logout">
                  <LogOut size={12} />
                </button>
              </div>
            )}
            {clientSession && (
              <div className="sm:hidden flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#D4AF37] text-[#0B0E17] text-xs font-bold" data-testid="mpire-bucks-widget-mobile">
                <DollarSign size={10} strokeWidth={3} />
                <span className="tabular-nums" data-testid="mpire-bucks-balance-mobile">${Number(clientSession.mpire_bucks_balance || 0).toFixed(2)}</span>
              </div>
            )}
            {clientSession && (() => {
              const initials = (clientSession.name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
              const src = clientSession.photo_url
                ? (String(clientSession.photo_url).startsWith("/api") ? `${API}${clientSession.photo_url}` : clientSession.photo_url)
                : null;
              return (
                <div
                  className="w-9 h-9 rounded-full overflow-hidden shrink-0 flex items-center justify-center ring-2 ring-[#D4AF37]/60 shadow-[0_2px_10px_rgba(212,175,55,0.35)]"
                  title={clientSession.name}
                  data-testid="client-avatar"
                >
                  {src ? (
                    <img src={src} alt={clientSession.name} className="w-full h-full object-cover" data-testid="client-avatar-image" />
                  ) : (
                    <span className="w-full h-full flex items-center justify-center bg-[#D4AF37] text-[#0B0E17] text-xs font-extrabold tracking-tight" data-testid="client-avatar-initials">
                      {initials}
                    </span>
                  )}
                </div>
              );
            })()}
            {!isDP && <CartDrawer
              clientEmailPrefill={selectedClient?.email || ""}
              clientNamePrefill={selectedClient?.name || ""}
              clientId={clientId || null}
              source="wholesale"
              readOnly={!canPlaceOrders}
              readOnlyLabel={orderingLabel}
              triggerClassName="bg-[#D4AF37] hover:bg-[#c39f2c] text-[#0B0E17] font-bold border-[#D4AF37] shadow-[0_2px_12px_rgba(212,175,55,0.45)]"
              badgeClassName="bg-white text-[#0B0E17] border border-[#D4AF37]"
            />}
          </div>
        </header>

        {/* Members-only hero banner */}
        <div className="border-b border-[#2A2F3C] bg-gradient-to-r from-[#12141C] via-[#171B26] to-[#12141C]" data-testid="wholesale-banner">
          <div className="px-4 sm:px-6 lg:px-8 py-7 sm:py-10">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.2em]">
                <Lock size={11} /> Members Only
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold uppercase tracking-[0.15em]" data-testid="verified-badge">
                <ShieldCheck size={11} /> Verified Client Access
              </span>
            </div>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-none">
              Wholesale <span className="text-[#D4AF37]">Portal</span>
            </h1>
            <p className="text-gray-400 mt-3 max-w-2xl text-sm sm:text-base">
              You're in the privileged space — <span className="text-[#F5D06B] font-semibold">exclusive discounted pricing</span>, live inventory, and Due Upon Receipt terms. Pay by card for instant checkout, or invoice a saved client account.
            </p>
          </div>
        </div>

        {/* Body — grid + Net-30 rail */}
        <section className="flex-1 px-4 sm:px-6 lg:px-8 py-6 pb-28 lg:pb-8">
          {cat && (
            <div className="mb-4 inline-flex items-center gap-2 text-xs text-gray-300 bg-[#171B26] border border-[#2A2F3C] px-3 py-1.5 rounded-full">
              <span className="capitalize">Filtered by <strong className="text-[#D4AF37]">{cat}</strong></span>
              <button onClick={() => setCat("")} className="text-[#D4AF37] font-semibold hover:underline" data-testid="clear-filter">Clear</button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 lg:gap-8 items-start">
            {/* Products */}
            <div>
              <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-3 sm:gap-4" data-testid="wholesale-grid">
                {paged.map((p) => <ProductCard
                  key={p.id}
                  p={p}
                  available={avail[p.id]?.available ?? p.warehouse_stock}
                  isDP={isDP}
                  isRegisteredClient={isRegisteredClient}
                  canPlaceOrders={canPlaceOrders}
                  invoiceQty={Number(invoiceQty[p.id] || 0)}
                  onAddToCart={addToCart}
                  onInvoiceQtyChange={updateInvoiceQty}
                />)}
                {paged.length === 0 && (
                  <p className="col-span-full text-center text-gray-500 py-16">No products match your search.</p>
                )}
              </div>
              <div className="mt-6 rounded-md border border-[var(--border)] overflow-hidden bg-white">
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  totalItems={filtered.length}
                  pageSize={PAGE_SIZE}
                  testid="wholesale-pagination"
                />
              </div>
            </div>

            {/* Net-30 sidebar — desktop */}
            {!isRegisteredClient && <aside className="hidden lg:block">
              <div className="rounded-xl p-5 bg-[#171B26] border border-[#2A2F3C] sticky top-24" data-testid="net30-sidebar">
                <Net30Panel />
              </div>
            </aside>}
          </div>
        </section>

        <footer className="mt-auto border-t border-[#2A2F3C] py-6 text-center text-xs text-gray-500">
          Secure card checkout by Stripe · Due Upon Receipt invoicing available for established clients
        </footer>
      </main>

      {/* Mobile: sticky Net-30 bar */}
      {!isRegisteredClient && <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-[#0B0E17]/95 backdrop-blur border-t border-[#2A2F3C] px-4 py-3" data-testid="net30-mobile-bar">
        <div className="flex items-center justify-between gap-3">
          <div className="leading-tight">
            <p className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-bold">Due Upon Receipt Invoice</p>
            <p className="text-sm text-gray-200"><span className="text-[#F5D06B] font-bold tabular-nums">${invoiceTotal.toFixed(2)}</span> · {invoiceLineCount} item{invoiceLineCount === 1 ? "" : "s"}</p>
          </div>
          <Button onClick={() => setMobileInvoiceOpen(true)} className="bg-[#D4AF37] hover:bg-[#c39f2c] text-[#0B0E17] font-bold" data-testid="open-mobile-invoice">
            <FileText size={14} className="mr-1.5" /> Review & place
          </Button>
        </div>
      </div>}

      {/* Mobile: Net-30 dialog */}
      {!isRegisteredClient && <Dialog open={mobileInvoiceOpen} onOpenChange={setMobileInvoiceOpen}>
        <DialogContent className="bg-[#171B26] border-[#2A2F3C] text-gray-100" data-testid="net30-mobile-dialog">
          <DialogHeader><DialogTitle className="text-white">Due Upon Receipt Invoice Order</DialogTitle></DialogHeader>
          <Net30Panel />
        </DialogContent>
      </Dialog>}
    </div>
  );
}

export default function WholesaleOrder() {
  return <CartProvider><WholesaleBody /></CartProvider>;
}
